import * as React from "react";
import { Pressable, Text, View } from "react-native";
import { Link, useRouter } from "expo-router";
import { Feather } from "@expo/vector-icons";
import { ChevronsUpDownIcon } from "lucide-react-native";
import { observer } from "mobx-react-lite";

import { useCurrentAccount } from "~/hooks/useCurrentAccount";
import { icons } from "~/lib/constants";
import { cn } from "~/lib/utils";
import { useGlobalStore } from "~/stores/global";
import { AccountType } from "~/stores/global/store";
//import { Dropdown } from "~/ui/Dropdown";
import * as SD from "~/ui/SheetDropdown";
import { useAccountActions } from "./AccountActions/context";
import { AccountAvatar } from "./AccountAvatar";

export const AccountSwitcher = observer(function AccountSwitcher() {
  const { store } = useGlobalStore(true);
  const currentAccount = useCurrentAccount();
  const { push } = useRouter();
  const personalAccount = store.personalAccount!;
  const organizations = store.organizations;
  const { openCreateTeam } = useAccountActions();
  const isTeam = currentAccount.id !== personalAccount.id;
  return (
    <View className="flex-row items-center gap-1">
      <SD.Root modal={false}>
        <SD.Trigger asChild>
          <Pressable
            role="button"
            className="hover:bg-surface-active group h-9 flex-row items-center justify-center gap-1 rounded-md pl-2 pr-1"
          >
            <View className="flex-row items-center gap-2">
              <AccountAvatar
                size={24}
                avatar={currentAccount.avatarUrl}
                fallbackText={currentAccount.displayName || currentAccount.slug}
              />
              <Text className="text-text-primary text-sm">
                {currentAccount.displayName}
              </Text>
            </View>
            <ChevronsUpDownIcon
              className="text-input-icon"
              size={20}
              strokeWidth={1.5}
            />
          </Pressable>
        </SD.Trigger>
        <SD.Content
          className="flex min-w-64 flex-col gap-3"
          align="start"
          alignOffset={-16}
          side="bottom"
          sideOffset={16}
        >
          {isTeam && (
            <SD.Group>
              <SD.Item
                onSelect={() => push(`/${currentAccount.slug}/settings`)}
                className={cn(
                  "hover:bg-surface-tertiary flex cursor-pointer flex-row items-center gap-2 rounded-md p-2 outline-none",
                )}
              >
                <Text className="text-text-primary">
                  {isTeam ? "Team" : "Account"} settings
                </Text>
              </SD.Item>
              <SD.Item
                onSelect={() =>
                  push(`/${currentAccount.slug}/settings#members`)
                }
                className={cn(
                  "hover:bg-surface-tertiary flex cursor-pointer flex-row items-center gap-2 rounded-md p-2 outline-none",
                )}
              >
                <Text className="text-text-primary">
                  Invite and manage members
                </Text>
              </SD.Item>
              <SD.Separator className="border-input-border -mx-2 mt-3 border-b py-0" />
            </SD.Group>
          )}
          <SD.Group>
            <SD.Label className="my-3">Personal Account</SD.Label>
            <AccountItem
              account={personalAccount}
              isSelected={personalAccount.id === currentAccount.id}
            />
          </SD.Group>
          <SD.Group>
            <SD.Label className="my-3">Teams</SD.Label>
            {Array.from(organizations.values()).map((org) => (
              <AccountItem
                key={org.id}
                account={org}
                isSelected={org.id === currentAccount.id}
              />
            ))}
            <SD.Item
              onSelect={() =>
                openCreateTeam({ onSuccess: (slug) => undefined })
              }
              className="hover:bg-surface-tertiary flex cursor-pointer flex-row items-center gap-2 rounded-md p-2 outline-none"
            >
              <Feather
                name="plus-circle"
                className="text-button-primary"
                size={24}
              />
              <Text className="text-text-primary text-sm">
                Create a new team
              </Text>
            </SD.Item>
          </SD.Group>
        </SD.Content>
      </SD.Root>
    </View>
  );
});

const AccountItem = React.forwardRef<
  HTMLDivElement,
  {
    account: AccountType;
    isSelected: boolean;
  }
>(function AccountItem({ account, isSelected }, ref) {
  return (
    <SD.Item ref={ref} asChild>
      <Link
        href={`/${account.slug}/`}
        className={cn(
          "flex flex-row items-center gap-2 rounded-md p-2 outline-none",
          isSelected ? "bg-surface-active" : "hover:bg-surface-tertiary",
        )}
      >
        <View className="flex-1 flex-row items-center gap-2">
          <AccountAvatar
            avatar={account.avatarUrl}
            fallbackText={account.displayName || account.slug}
            size={24}
          />

          <Text className="text-text-primary text-sm">
            {account.displayName}
          </Text>
        </View>
        {isSelected && <SelectedIndicator />}
      </Link>
    </SD.Item>
  );
});

const SelectedIndicator = () => (
  <View className="bg-button-primary size-4 items-center justify-center rounded-full">
    <Feather name="check" className="text-button-label" size={10} />
  </View>
);
